import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { clearSelections } from '../redux/slices/bookingSlice';
import { logout } from '../redux/slices/authSlice';
import eventService from '../services/eventService';
import api from '../services/api';
import Swal from 'sweetalert2';
import RegistrationStepperHeader from '../components/RegistrationStepperHeader';
import SuccessModal from '../components/SuccessModal';

const EventPayment = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { selectedEvent, selectedCenter } = useSelector((state) => state.event);
  const {
    selectedService,
    selectedOptionalServices,
    selectedTimeOption,
    devoteeInfo,
    familyMembers,
    selectedFamilyMemberIds,
    totalPrice,
  } = useSelector((state) => state.booking);

  const [paymentMethod, setPaymentMethod] = useState('credit');
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
  
  console.log('EventPayment - RECAPTCHA_SITE_KEY:', RECAPTCHA_SITE_KEY);
  
  const [formData, setFormData] = useState({
    cardholderName: `${user?.User_First_Name || user?.user_First_Name || ''} ${user?.User_Last_Name || user?.user_Last_Name || ''}`.trim(),
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    billingZipCode: '',
    accountHolderName: '',
    accountType: 'checking',
    routingNumber: '',
    accountNumber: '',
    bankName: '',
  });
  const [errors, setErrors] = useState({});
  const [isAgreementChecked, setIsAgreementChecked] = useState(false);
  const [isAgreementModalOpen, setIsAgreementModalOpen] = useState(false);

  useEffect(() => {
    // Don't show incomplete booking popup if success modal is showing
    if (showSuccessModal) {
      return;
    }

    if (!isAuthenticated) {
      Swal.fire({
        title: 'Login Required',
        text: 'Please login to complete payment',
        icon: 'warning',
        confirmButtonText: 'Go to Login',
      }).then(() => {
        navigate('/');
      });
      return;
    }

    // Removed incomplete booking check - users reach this page through proper navigation flow
    // The routing and stepper components handle the flow validation
  }, [isAuthenticated, navigate, showSuccessModal]);

  // Load and render Google reCAPTCHA v2
  useEffect(() => {
    const ensureScript = () => {
      return new Promise((resolve) => {
        const w = window;
        if (w.grecaptcha) return resolve();
        const existing = document.querySelector('script[src^="https://www.google.com/recaptcha/api.js"]');
        if (existing) {
          existing.addEventListener('load', () => resolve());
          return;
        }
        const s = document.createElement('script');
        s.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        document.body.appendChild(s);
      });
    };

    const renderWidget = () => {
      const w = window;
      const el = document.getElementById('recaptcha-container-event-payment');
      console.log('EventPayment - reCAPTCHA container element:', el);
      console.log('EventPayment - reCAPTCHA widgetId:', recaptchaWidgetId);
      console.log('EventPayment - grecaptcha available:', !!w.grecaptcha);
      
      if (!el) {
        console.warn('EventPayment - reCAPTCHA container not found!');
        return;
      }
      if (recaptchaWidgetId !== null) {
        console.log('EventPayment - reCAPTCHA already rendered');
        return;
      }
      
      // Clear any existing content in the container
      el.innerHTML = '';

      const pollUntilReady = () => {
        const api = (w && w.grecaptcha) ? w.grecaptcha : null;
        if (!api) {
          console.log('EventPayment - Waiting for grecaptcha API...');
          setTimeout(pollUntilReady, 150);
          return;
        }
        console.log('EventPayment - grecaptcha API ready');
        const doRender = () => {
          if (recaptchaWidgetId !== null) return;
          if (typeof api.render !== 'function') {
            console.log('EventPayment - Waiting for grecaptcha.render...');
            setTimeout(doRender, 150);
            return;
          }
          try {
            console.log('EventPayment - Rendering reCAPTCHA with site key:', RECAPTCHA_SITE_KEY);
            const id = api.render(el, {
              sitekey: RECAPTCHA_SITE_KEY,
              size: 'normal',
              theme: 'light',
              callback: (token) => {
                console.log('EventPayment - reCAPTCHA token received');
                setRecaptchaToken(token);
              },
              'expired-callback': () => {
                console.log('EventPayment - reCAPTCHA expired');
                setRecaptchaToken('');
              },
              'error-callback': (error) => {
                console.error('EventPayment - reCAPTCHA error:', error);
                console.error('EventPayment - Site key used:', RECAPTCHA_SITE_KEY);
                console.error('EventPayment - Current domain:', window.location.hostname);
                setRecaptchaToken('');
                // Show user-friendly message
                setErrors(prev => ({ 
                  ...prev, 
                  recaptcha: 'reCAPTCHA verification failed. Please refresh the page and try again.' 
                }));
              },
            });
            console.log('EventPayment - reCAPTCHA rendered with ID:', id);
            setRecaptchaWidgetId(id);
          } catch (e) {
            console.warn('EventPayment - reCAPTCHA render failed, retrying...', e);
            setTimeout(doRender, 200);
          }
        };
        doRender();
      };

      pollUntilReady();
    };

    let mounted = true;
    ensureScript().then(() => {
      if (!mounted) return;
      setTimeout(renderWidget, 0);
    });
    return () => {
      mounted = false;
    };
  }, [RECAPTCHA_SITE_KEY, recaptchaWidgetId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'cardNumber') {
      const digits = value.replace(/\D/g, '');
      const parts = [];
      for (let i = 0; i < digits.length; i += 4) {
        parts.push(digits.slice(i, i + 4));
      }
      formattedValue = parts.join(' ').trim();
    } else if (name === 'expiryDate') {
      const digits = value.replace(/\D/g, '');
      if (digits.length <= 2) {
        formattedValue = digits;
      } else {
        formattedValue = digits.slice(0, 2) + '/' + digits.slice(2, 4);
      }
    } else if (name === 'cvv') {
      formattedValue = value.replace(/\D/g, '').slice(0, 4);
    } else if (name === 'billingZipCode') {
      // Format zip code: 12345 or 12345-6789
      const digits = value.replace(/\D/g, '');
      if (digits.length <= 5) {
        formattedValue = digits;
      } else {
        formattedValue = digits.slice(0, 5) + '-' + digits.slice(5, 9);
      }
    } else if (name === 'routingNumber') {
      formattedValue = value.replace(/\D/g, '').slice(0, 9);
    } else if (name === 'accountNumber') {
      formattedValue = value.replace(/\D/g, '');
    }

    setFormData((prev) => ({ ...prev, [name]: formattedValue }));
    
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateCreditCard = () => {
    const newErrors = {};
    
    if (!formData.cardholderName || formData.cardholderName.trim().length === 0) {
      newErrors.cardholderName = 'Cardholder name is required';
    }
    
    const cardDigits = formData.cardNumber.replace(/\D/g, '');
    if (!cardDigits) {
      newErrors.cardNumber = 'Card number is required';
    } else if (cardDigits.length < 13 || cardDigits.length > 19) {
      newErrors.cardNumber = 'Card number must be 13-19 digits';
    }
    
    if (!formData.expiryDate) {
      newErrors.expiryDate = 'Expiry date is required';
    } else {
      const expiryMatch = formData.expiryDate.match(/^(\d{2})\/(\d{2})$/);
      if (!expiryMatch) {
        newErrors.expiryDate = 'Use MM/YY format';
      } else {
        const month = parseInt(expiryMatch[1], 10);
        const year = parseInt(expiryMatch[2], 10);
        if (month < 1 || month > 12) {
          newErrors.expiryDate = 'Invalid month (01-12)';
        } else {
          const now = new Date();
          const currentYear = now.getFullYear() % 100;
          const currentMonth = now.getMonth() + 1;
          if (year < currentYear || (year === currentYear && month < currentMonth)) {
            newErrors.expiryDate = 'Card has expired';
          }
        }
      }
    }
    
    if (!formData.cvv) {
      newErrors.cvv = 'CVV is required';
    } else if (formData.cvv.length < 3 || formData.cvv.length > 4) {
      newErrors.cvv = 'CVV must be 3-4 digits';
    }
    
    if (!formData.billingZipCode) {
      newErrors.billingZipCode = 'Billing zip code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.billingZipCode)) {
      newErrors.billingZipCode = 'Invalid zip code (use 5 digits or 5+4 format)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateACH = () => {
    const newErrors = {};
    
    if (!formData.accountHolderName || formData.accountHolderName.trim().length < 2) {
      newErrors.accountHolderName = 'Account holder name is required (min 2 characters)';
    }
    
    if (!formData.routingNumber) {
      newErrors.routingNumber = 'Routing number is required';
    } else if (formData.routingNumber.length !== 9) {
      newErrors.routingNumber = 'Routing number must be exactly 9 digits';
    }
    
    if (!formData.accountNumber) {
      newErrors.accountNumber = 'Account number is required';
    } else if (formData.accountNumber.length < 5 || formData.accountNumber.length > 17) {
      newErrors.accountNumber = 'Account number must be 5-17 digits';
    }
    
    if (!formData.bankName || formData.bankName.trim().length < 2) {
      newErrors.bankName = 'Bank name is required';
    }
    
    if (!formData.billingZipCode) {
      newErrors.billingZipCode = 'Billing zip code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.billingZipCode)) {
      newErrors.billingZipCode = 'Invalid zip code (use 5 digits or 5+4 format)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAgreementChecked) {
      Swal.fire({
        icon: 'warning',
        title: 'Agreement Required',
        text: 'Please review and agree to the terms and conditions before proceeding with payment.',
        confirmButtonColor: '#7c3aed',
      });
      return;
    }

    if (!recaptchaToken) {
      setErrors({ recaptcha: 'Please complete the reCAPTCHA verification' });
      document.getElementById('recaptcha-container-event-payment')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const isValid = paymentMethod === 'credit' ? validateCreditCard() : validateACH();
    if (!isValid) return;

    setLoading(true);

    // Track step statuses for success modal (declare outside try block so it's accessible in catch)
    const stepStatus = {
      payment: { success: false, message: '' },
      registration: { success: false, message: '' },
      email: { success: false, message: '' }
    };
    
    let transactionId = null;
    let paymentSucceeded = false;
    
    try {
      console.log('Step 1: Processing payment via Authorize.Net...');
      
      // Extract user phone number from devoteeInfo (stored during devotee details step)
      // For guest users, phone is captured during guest login and stored in devoteeInfo
      const userPhone = devoteeInfo.phone || 
                        user?.userData?.User_Cell_Phone_No || 
                        user?.userData?.user_Cell_Phone_No || 
                        user?.userData?.User_Phone || 
                        user?.userData?.user_Phone ||
                        user?.User_Cell_Phone_No || 
                        user?.user_Cell_Phone_No || 
                        user?.User_Phone || 
                        user?.user_Phone || '';
      console.log('User phone number extracted:', userPhone);
      console.log('Phone source - devoteeInfo.phone:', devoteeInfo.phone);
      console.log('User object for phone extraction:', {
        userData: user?.userData,
        userDataPhone: user?.userData?.User_Cell_Phone_No || user?.userData?.user_Cell_Phone_No || user?.userData?.User_Phone
      });
      
      // Validate required fields before payment
      if (!devoteeInfo.firstName || !devoteeInfo.lastName || !devoteeInfo.email) {
        throw new Error('Customer information is incomplete. Please go back and complete all required fields.');
      }
      
      if (!userPhone) {
        throw new Error('Phone number is required. Please go back and enter your phone number.');
      }
      
      // Use cardholder name as fallback for customer name if needed
      const customerFirstName = devoteeInfo.firstName || formData.cardholderName.split(' ')[0] || 'Guest';
      const customerLastName = devoteeInfo.lastName || formData.cardholderName.split(' ').slice(1).join(' ') || 'User';
      
      const paymentPayload = {
        app: "Events",
        entity: "RMS",
        paymentType: paymentMethod === 'credit' ? 'cc' : 'ach',
        customerEmail: devoteeInfo.email,
        orderId: "0",
        amount: totalPrice.toString(),
        description: `Event Registration: ${selectedEvent.event_Name} - ${customerFirstName} ${customerLastName}`,
        customer: {
          firstName: customerFirstName,
          lastName: customerLastName,
          email: devoteeInfo.email,
          phone: userPhone,
          company: "string",
          notes: `Event: ${selectedEvent.event_Name}, Service: ${selectedService.service_Name}`,
          customerAccount: "string",
          shippingSameAsBilling: true,
          billingAddress: {
            streetAddress1: devoteeInfo.homeAddress || '',
            streetAddress2: "-",
            city: "",
            stateCode: "",
            zipCode: formData.billingZipCode || "",
            country: "US"
          },
          shippingAddress: {
            streetAddress1: devoteeInfo.homeAddress || '',
            streetAddress2: "-",
            city: "",
            stateCode: "",
            zipCode: formData.billingZipCode || "",
            country: "US"
          }
        },
        creditcard: {
          creditCardNumber: paymentMethod === 'credit' ? formData.cardNumber.replace(/\s/g, '') : "string",
          expirationDate: paymentMethod === 'credit' ? formData.expiryDate : "string",
          issuer: "string",
          billingZipCode: formData.billingZipCode || "",
          customerId: "string",
          cvv: paymentMethod === 'credit' ? formData.cvv : "string"
        },
        ach: {
          isCheckingAccount: paymentMethod === 'ach' ? (formData.accountType === 'checking' ? 'true' : 'false') : "string",
          routingNumber: paymentMethod === 'ach' ? formData.routingNumber : "string",
          accountNumber: paymentMethod === 'ach' ? formData.accountNumber : "string",
          bankName: paymentMethod === 'ach' ? formData.bankName : "string",
          customerId: "string",
          isDefault: "string"
        },
        recurr: {
          paymentAmount: "string",
          accountId: "string",
          startDate: "string",
          executionFrequencyType: "string",
          executionFrequencyParameter: "string",
          endDate: "string",
          paymentSubType: "string",
          invoiceNumber: "string",
          firstPaymentAmount: "string",
          firstPaymentDate: "string",
          description: "string"
        }
      };
      
      console.log('=== PAYMENT PAYLOAD DEBUG ===');
      console.log('Payment payload:', paymentPayload);
      console.log('Customer info:', {
        firstName: customerFirstName,
        lastName: customerLastName,
        email: devoteeInfo.email,
        phone: userPhone
      });
      console.log('Payment method:', paymentMethod);
      console.log('Amount:', totalPrice);
      console.log('============================');
      
      const paymentResponse = await api.post('/PaymentChannel/AuthNetOneTimePayment', paymentPayload);
      console.log('Payment response received:', paymentResponse);

      // Axios wraps response in data object
      const paymentData = paymentResponse.data;
      console.log('Payment data:', paymentData);

      const isApproved = paymentData?.createTransactionResponse?.transactionResponse?.responseCode === "1";
      console.log('Payment approved status:', isApproved);
      
      if (!isApproved) {
        // Extract error message from various possible formats (matching class module pattern)
        let errorMessage = 'Payment failed';
        
        // Check for meta.errors structure (Authorize.Net error format from backend)
        if (paymentData?.meta?.errors?.errorMessages?.length > 0) {
          errorMessage = paymentData.meta.errors.errorMessages[0].message;
        } else if (paymentData?.message) {
          errorMessage = paymentData.message;
        } else if (paymentData?.createTransactionResponse?.transactionResponse?.messages?.length > 0) {
          errorMessage = paymentData.createTransactionResponse.transactionResponse.messages[0].description || 
                        paymentData.createTransactionResponse.transactionResponse.messages[0].text;
        } else if (paymentData?.createTransactionResponse?.transactionResponse?.errors?.length > 0) {
          errorMessage = paymentData.createTransactionResponse.transactionResponse.errors[0].errorText;
        }
        
        throw new Error(errorMessage);
      }

      transactionId = paymentData.createTransactionResponse.transactionResponse.transId;
      console.log('✅ Payment approved! Transaction ID:', transactionId);
      
      stepStatus.payment.success = true;
      stepStatus.payment.message = 'Payment processed successfully';
      paymentSucceeded = true;

      console.log('Step 2: Creating event registration...');
      
      // Extract User_Id from authenticated user
      const userId = user?.User_Id || user?.user_Id || user?.userId || user?.userData?.User_Id || user?.userData?.user_Id;
      console.log('User ID for registration:', userId);
      
      // Calculate family count early since it's needed for registration
      const familyCount = parseInt(devoteeInfo.numberOfFamilyMembers) || 0;
      console.log('Family count for registration:', familyCount);
      
      console.log('Registration data:', {
        User_Id: userId,
        Event_Id: selectedEvent.event_Id,
        Center_Id: selectedCenter.center_Id,
        Event_Service_Id: selectedService.service_Id,
        Event_Time_Option_Id: selectedTimeOption.event_Time_Option_Id,
        First_Name: devoteeInfo.firstName,
        Last_Name: devoteeInfo.lastName,
        Email: devoteeInfo.email,
        Number_Of_Family_Members: familyCount
      });
      
      const registration = await eventService.postEventRegistration({
        User_Id: userId,
        Event_Id: selectedEvent.event_Id,
        Center_Id: selectedCenter.center_Id,
        Event_Service_Id: selectedService.service_Id,
        Event_Time_Option_Id: selectedTimeOption.event_Time_Option_Id,
        First_Name: devoteeInfo.firstName,
        Last_Name: devoteeInfo.lastName,
        Email: devoteeInfo.email,
        Telephone: userPhone,
        Home_Address: devoteeInfo.homeAddress,
        Gothram: devoteeInfo.gothram || '',
        Total_Amount: totalPrice,
        Payment_Status: 'Paid',
        Transaction_Id: transactionId,
        Number_Of_Family_Members: familyCount,
        Created_By: devoteeInfo.email,
        Status: 'Y'
      });

      console.log('Registration response:', registration);
      const registrationId = registration.Event_Registration_Id || registration.event_Registration_Id;
      console.log('✅ Registration created! ID:', registrationId);

      console.log('Step 3: Creating payment record...');
      const now = new Date();
      const paymentRecord = await eventService.postEventPayment({
        Event_Registration_Id: registrationId,
        User_Id: userId,
        Total_Amount_Paid: totalPrice,
        Payment_Method: paymentMethod === 'credit' ? 'Credit Card' : 'ACH',
        Payment_Date: now.toISOString(),
        Payment_Time: now.toTimeString().split(' ')[0], // HH:MM:SS format
        Payment_Status: 'Y',
        Payment_Provider_Transaction_Id: transactionId,
        Payment_Comments: JSON.stringify(paymentData),
        Created_By: devoteeInfo.email
      });
      console.log('✅ Payment record created:', paymentRecord);
      
      stepStatus.registration.success = true;
      stepStatus.registration.message = 'Registration and payment records saved successfully';

      console.log('Step 4: Creating attendees...');
      console.log('=== ATTENDEE COUNT DEBUG ===');
      console.log('familyMembers:', familyMembers);
      console.log('selectedFamilyMemberIds:', selectedFamilyMemberIds);
      console.log('devoteeInfo.numberOfFamilyMembers:', devoteeInfo.numberOfFamilyMembers);
      
      // familyCount already declared earlier for registration
      const selectedMembers = familyMembers.filter(m => selectedFamilyMemberIds.includes(m.id));
      
      console.log('Family count from devoteeInfo:', familyCount);
      console.log('Selected family members count:', selectedMembers.length);
      console.log('Selected family members:', selectedMembers);
      console.log('Total attendees (1 devotee + family):', 1 + familyCount);
      console.log('===========================');
      
      for (const student of selectedMembers) {
        try {
          const attendee = await eventService.postEventAttendee({
            Event_Registration_Id: registrationId,
            Student_Id: student.id,
            Attendee_Name: `${student.firstName} ${student.lastName}`,
            Nakshatram: student.nastram || '',
            Status: 'Y',
            Created_By: devoteeInfo.email
          });
          console.log(`✅ Attendee created for ${student.firstName} ${student.lastName}:`, attendee);
        } catch (attendeeError) {
          console.error(`❌ Failed to create attendee for ${student.firstName}:`, attendeeError);
        }
      }

      console.log('Step 5: Sending confirmation email...');
      
      // Prepare email data
      const emailTimestamp = new Date();
      const transactionDate = emailTimestamp.toLocaleDateString();
      const transactionTime = emailTimestamp.toLocaleTimeString();
      const eventDate = selectedEvent.event_Date ? new Date(selectedEvent.event_Date).toLocaleDateString() : transactionDate;
      
      const emailData = {
        emailFrom: "string",
        emailBody: "string",
        emailSubject: "string",
        attachment: "Y",
        userPhoneNumber: userPhone,
        emailTo: devoteeInfo.email,
        location: "YKEvent",
        input: "string",
        appContent: {
          userName: `${devoteeInfo.firstName} ${devoteeInfo.lastName}`,
          programName: selectedEvent.event_Name,
          appSchedule: selectedTimeOption.time_Option_Name,
          attendee: familyCount > 0 
            ? `${devoteeInfo.firstName} ${devoteeInfo.lastName} + ${familyCount} family member${familyCount > 1 ? 's' : ''}`
            : `${devoteeInfo.firstName} ${devoteeInfo.lastName}`,
          numberOfAttendees: (1 + familyCount).toString(),
          date: transactionDate,
          time: transactionTime,
          orderType: "Event Registration",
          orderNo: registrationId.toString(),
          paymentMode: paymentMethod === 'credit' ? 'Credit Card' : 'ACH',
          amount: totalPrice.toFixed(2),
          email: devoteeInfo.email,
          phone: userPhone,
          phoneNumber: userPhone,
          contactNumber: userPhone,
          pickupPerson: `${devoteeInfo.firstName} ${devoteeInfo.lastName}`,
          pickupPersonContact: userPhone,
          instructions: selectedService.service_Description || 'N/A',
          itemData: selectedService.service_Name,
          pickUpDate: eventDate,
          pickUpTime: selectedTimeOption.time_Option_Name,
          eventDate: eventDate,
          eventTime: selectedTimeOption.time_Option_Name,
          timeSlot: selectedTimeOption.time_Option_Name,
          paymentType: paymentMethod === 'credit' ? 'Credit Card' : 'ACH',
          billingAddress: devoteeInfo.homeAddress || 'Not provided',
          address: devoteeInfo.homeAddress || 'Not provided',
          pickupOrReceiver: `${devoteeInfo.firstName} ${devoteeInfo.lastName}`,
          orderReady: selectedTimeOption.time_Option_Name,
          orderReadyLastLine: `Thank you for registering for ${selectedEvent.event_Name}`,
          headingDate: eventDate,
          headingTime: selectedTimeOption.time_Option_Name,
          transactionId: transactionId,
          transactionDate: transactionDate,
          transactionTime: transactionTime
        }
      };

      try {
        console.log('=== EMAIL DATA DEBUG ===');
        console.log('Full emailData object:', JSON.stringify(emailData, null, 2));
        console.log('appContent fields:', Object.keys(emailData.appContent));
        console.log('userPhone value:', userPhone);
        console.log('transactionDate:', transactionDate);
        console.log('transactionTime:', transactionTime);
        console.log('eventDate:', eventDate);
        console.log('========================');
        
        const emailResponse = await api.post(
          '/messageChannel/SendEmailMessage?appName=Events&templateName=YKEventPayment&orgId=3',
          emailData
        );
        
        console.log('Confirmation email sent successfully');
        console.log('Email response with receipt URL:', emailResponse);
        
        stepStatus.email.success = true;
        stepStatus.email.message = 'Confirmation email sent successfully';
        
        // Extract receipt URL from various possible response formats
        let receiptUrl = '';
        if (typeof emailResponse === 'string') {
          receiptUrl = emailResponse;
        } else if (typeof emailResponse?.data === 'string') {
          receiptUrl = emailResponse.data;
        } else {
          receiptUrl = (
            emailResponse?.receiptUrl || emailResponse?.ReceiptUrl || emailResponse?.receiptURL ||
            emailResponse?.attachment || emailResponse?.Attachment ||
            emailResponse?.attachmentUrl || emailResponse?.AttachmentUrl || emailResponse?.attachmentURL ||
            emailResponse?.pdfUrl || emailResponse?.pdfURL ||
            emailResponse?.fileUrl || emailResponse?.fileURL ||
            emailResponse?.url || emailResponse?.Url || emailResponse?.URL ||
            emailResponse?.Response || emailResponse?.response ||
            // nested under data
            emailResponse?.data?.receiptUrl || emailResponse?.data?.ReceiptUrl || emailResponse?.data?.receiptURL ||
            emailResponse?.data?.attachment || emailResponse?.data?.Attachment ||
            emailResponse?.data?.attachmentUrl || emailResponse?.data?.AttachmentUrl || emailResponse?.data?.attachmentURL ||
            emailResponse?.data?.pdfUrl || emailResponse?.data?.pdfURL ||
            emailResponse?.data?.fileUrl || emailResponse?.data?.fileURL ||
            emailResponse?.data?.url || emailResponse?.data?.Url || emailResponse?.data?.URL ||
            emailResponse?.data?.Response || emailResponse?.data?.response ||
            ''
          );
        }
        console.log('Receipt URL extracted:', receiptUrl);
        
        // Store payment result with receipt URL
        console.log('=== SETTING PAYMENT RESULT ===');
        console.log('familyCount:', familyCount);
        console.log('numberOfAttendees being set:', 1 + familyCount);
        console.log('==============================');
        
        setPaymentResult({
          transactionId: transactionId,
          eventName: selectedEvent.event_Name,
          serviceName: selectedService.service_Name,
          timeSlot: selectedTimeOption.time_Option_Name,
          numberOfAttendees: 1 + familyCount,
          attendees: [{ firstName: devoteeInfo.firstName, lastName: devoteeInfo.lastName }],
          totalAmount: totalPrice,
          transactionDate: new Date().toLocaleString(),
          receiptUrl: receiptUrl,
          paymentMethod: paymentMethod === 'credit' ? 'Credit Card' : 'ACH',
          email: devoteeInfo.email,
          stepStatus: stepStatus
        });
      } catch (emailError) {
        console.error('Failed to send confirmation email:', emailError);
        stepStatus.email.success = false;
        stepStatus.email.message = 'Failed to send confirmation email';
        
        // Store payment result even if email failed (no receipt URL)
        setPaymentResult({
          transactionId: transactionId,
          eventName: selectedEvent.event_Name,
          serviceName: selectedService.service_Name,
          timeSlot: selectedTimeOption.time_Option_Name,
          numberOfAttendees: 1 + familyCount,
          attendees: [{ firstName: devoteeInfo.firstName, lastName: devoteeInfo.lastName }],
          totalAmount: totalPrice,
          transactionDate: new Date().toLocaleString(),
          receiptUrl: null,
          paymentMethod: paymentMethod === 'credit' ? 'Credit Card' : 'ACH',
          email: devoteeInfo.email,
          stepStatus: stepStatus
        });
      }
      
      setShowSuccessModal(true);

      // Reset reCAPTCHA after successful payment
      try {
        const w = window;
        if (recaptchaWidgetId !== null && w.grecaptcha) {
          w.grecaptcha.reset(recaptchaWidgetId);
          setRecaptchaToken('');
        }
      } catch (e) {
        console.warn('Failed to reset reCAPTCHA:', e);
      }

      // Don't clear selections here - will be cleared when modal closes

    } catch (error) {
      console.error('Payment/Registration error:', error);
      
      // Extract error message from various possible error formats
      let displayMessage = 'An error occurred while processing your request.';
      
      if (error.response?.data?.meta?.errors?.errorMessages?.length > 0) {
        displayMessage = error.response.data.meta.errors.errorMessages[0].message;
      } else if (error.response?.data?.message) {
        displayMessage = error.response.data.message;
      } else if (error.message) {
        displayMessage = error.message;
      }
      
      // If payment succeeded but registration/email failed, show success modal with step status
      if (paymentSucceeded && transactionId) {
        console.log('⚠️ Payment succeeded but post-payment processing failed');
        
        // Mark registration as failed with error message
        stepStatus.registration.success = false;
        stepStatus.registration.message = displayMessage;
        
        // Store payment result with step status
        setPaymentResult({
          transactionId: transactionId,
          eventName: selectedEvent.event_Name,
          serviceName: selectedService.service_Name,
          timeSlot: selectedTimeOption.time_Option_Name,
          numberOfAttendees: 1 + (parseInt(devoteeInfo.numberOfFamilyMembers) || 0),
          attendees: [{ firstName: devoteeInfo.firstName, lastName: devoteeInfo.lastName }],
          totalAmount: totalPrice,
          transactionDate: new Date().toLocaleString(),
          receiptUrl: null,
          paymentMethod: paymentMethod === 'credit' ? 'Credit Card' : 'ACH',
          email: devoteeInfo.email,
          stepStatus: stepStatus
        });
        
        // Show success modal (it will display payment success and registration failure)
        setShowSuccessModal(true);
      } else {
        // Payment itself failed - show error
        Swal.fire({
          title: 'Payment Failed',
          text: displayMessage,
          icon: 'error',
          confirmButtonText: 'Try Again',
          confirmButtonColor: '#ef4444'
        });
      }
      
      // Reset reCAPTCHA on error
      try {
        const w = window;
        if (recaptchaWidgetId !== null && w.grecaptcha) {
          w.grecaptcha.reset(recaptchaWidgetId);
          setRecaptchaToken('');
        }
      } catch (e) {
        console.warn('Failed to reset reCAPTCHA:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 pb-24">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-[28px] bg-white/70 backdrop-blur-xl shadow-[0_26px_70px_-45px_rgba(15,23,42,0.55)] border border-white/60 mb-4">
            <RegistrationStepperHeader
              title={selectedEvent?.event_Name || 'Event Registration'}
              subtitle={selectedEvent?.event_Date ? `${new Date(selectedEvent.event_Date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}${selectedCenter?.center_Name ? ` • ${selectedCenter.center_Name}` : ''}` : null}
              backPath="/devotee"
              backLabel="Back"
            />

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Order Summary - Left Column */}
                <div className="lg:col-span-1">
                  <div className="rounded-2xl bg-white/85 border border-gray-200/70 shadow-sm p-5 lg:sticky lg:top-6">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">Order Summary</div>
                        <div className="text-xs text-gray-500">Review details before payment.</div>
                      </div>
                      <div className="inline-flex items-center rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-gray-700 border border-gray-200 shadow-sm">
                        Payment
                      </div>
                    </div>
                
                <div className="space-y-3 mt-4">
                  {/* Agreement Section - Top of Order Summary */}
                  <div className="rounded-xl bg-violet-50/50 border border-violet-200 p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isAgreementChecked}
                        onChange={(e) => setIsAgreementChecked(e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500 cursor-pointer flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-gray-800 leading-relaxed">
                          I agree to the event terms &amp; conditions
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setIsAgreementModalOpen(true);
                          }}
                          className="mt-1.5 text-[11px] font-semibold text-violet-700 hover:text-violet-800 hover:underline inline-flex items-center gap-1"
                        >
                          <span>📄</span>
                          <span>View Full Agreement</span>
                        </button>
                        {!isAgreementChecked && (
                          <div className="mt-2.5 text-[10px] text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 flex items-start gap-2">
                            <span className="text-amber-600 text-sm flex-shrink-0">⚠️</span>
                            <span className="leading-relaxed">Please check this box to proceed with payment</span>
                          </div>
                        )}
                      </div>
                    </label>
                  </div>

                  <div className="flex justify-between items-start gap-3">
                    <span className="text-gray-700 text-sm flex-shrink-0">Event:</span>
                    <span className="text-gray-800 font-medium text-right text-sm break-words">
                      {selectedEvent?.event_Name}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-start gap-3">
                    <span className="text-gray-700 text-sm flex-shrink-0">Service:</span>
                    <span className="text-gray-800 font-medium text-right text-sm break-words">
                      {selectedService?.service_Name}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-start gap-3">
                    <span className="text-gray-700 text-sm flex-shrink-0">Time:</span>
                    <span className="text-gray-800 font-medium text-right text-sm break-words">
                      {selectedTimeOption?.time_Option_Name}
                      {selectedTimeOption?.time_Option_Description && (
                        <span className="block text-xs text-gray-600 mt-0.5">
                          {selectedTimeOption.time_Option_Description}
                        </span>
                      )}
                    </span>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-3">
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <span className="text-gray-700 text-sm font-semibold">Attendees</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 bg-blue-50 px-3 py-2 rounded-lg">
                        <span className="text-lg">👤</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-800 font-semibold text-xs truncate">{devoteeInfo.firstName} {devoteeInfo.lastName}</p>
                          <p className="text-gray-600 text-[10px]">Devotee</p>
                        </div>
                      </div>
                      {devoteeInfo.numberOfFamilyMembers > 0 && (
                        <div className="flex items-center gap-2 bg-purple-50 px-3 py-2 rounded-lg">
                          <span className="text-lg">�</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-gray-800 font-semibold text-xs">{devoteeInfo.numberOfFamilyMembers} Family {devoteeInfo.numberOfFamilyMembers === 1 ? 'Member' : 'Members'}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {selectedOptionalServices && selectedOptionalServices.length > 0 && (
                    <div className="border-t border-gray-200 pt-3">
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <span className="text-gray-700 text-sm font-semibold">Add-on Sevas</span>
                      </div>
                      <div className="space-y-2">
                        {selectedOptionalServices.map((addon, index) => (
                          <div key={index} className="flex items-center justify-between gap-2 bg-amber-50 px-3 py-2 rounded-lg">
                            <span className="text-gray-800 text-xs font-medium">{addon.service_Name || addon.optional_Service_Name}</span>
                            <span className="text-gray-900 text-xs font-semibold">${Number(addon.service_Price || addon.optional_Service_Price || 0).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                    <span className="text-gray-800 font-bold">Total:</span>
                    <span className="text-green-600 font-bold text-xl">${totalPrice?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>
                  </div>
                </div>

                {/* Payment Form - Right Column */}
                <div className="lg:col-span-2">
                  <form onSubmit={handleSubmit}>
                    <div className="rounded-2xl bg-white/85 border border-gray-200/70 shadow-sm p-5">
                      <div className="mb-4">
                        <div className="text-sm font-semibold text-gray-900 mb-3">Select Payment Method</div>
                        <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('credit')}
                        className={`flex-1 inline-flex items-center justify-center gap-2 rounded-full py-2.5 px-4 text-xs font-semibold border transition-all ${
                          paymentMethod === 'credit'
                            ? 'bg-violet-600 text-white border-violet-600 shadow-md'
                            : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <i className="fas fa-credit-card text-[11px]"></i>
                        Credit / Debit Card
                      </button>
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('ach')}
                        className={`flex-1 inline-flex items-center justify-center gap-2 rounded-full py-2.5 px-4 text-xs font-semibold border transition-all ${
                          paymentMethod === 'ach'
                            ? 'bg-violet-600 text-white border-violet-600 shadow-md'
                            : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <i className="fas fa-university text-[11px]"></i>
                        Bank Account (ACH)
                      </button>
                        </div>
                      </div>

                  <div className="space-y-4">
                    {paymentMethod === 'credit' ? (
                      <>
                        <div>
                          <h3 className="text-base font-semibold text-gray-800 mb-3">Card Information</h3>
                          
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Cardholder Name <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                name="cardholderName"
                                value={formData.cardholderName}
                                onChange={handleChange}
                                placeholder="Name as it appears on card"
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${
                                  errors.cardholderName
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-300 focus:ring-blue-500'
                                }`}
                                required
                              />
                              {errors.cardholderName && (
                                <p className="text-red-500 text-xs mt-1">{errors.cardholderName}</p>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Card Number <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                name="cardNumber"
                                value={formData.cardNumber}
                                onChange={handleChange}
                                placeholder="1234 5678 9012 3456"
                                maxLength="19"
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${
                                  errors.cardNumber
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-300 focus:ring-blue-500'
                                }`}
                                required
                              />
                              {errors.cardNumber && (
                                <p className="text-red-500 text-xs mt-1">{errors.cardNumber}</p>
                              )}
                            </div>
                          
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                  Expiry Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  name="expiryDate"
                                  value={formData.expiryDate}
                                  onChange={handleChange}
                                  placeholder="MM/YY"
                                  maxLength="5"
                                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${
                                    errors.expiryDate
                                      ? 'border-red-500 focus:ring-red-500'
                                      : 'border-gray-300 focus:ring-blue-500'
                                  }`}
                                  required
                                />
                                {errors.expiryDate && (
                                  <p className="text-red-500 text-xs mt-1">{errors.expiryDate}</p>
                                )}
                              </div>
                              <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                  CVV <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  name="cvv"
                                  value={formData.cvv}
                                  onChange={handleChange}
                                  placeholder="123"
                                  maxLength="4"
                                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${
                                    errors.cvv
                                      ? 'border-red-500 focus:ring-red-500'
                                      : 'border-gray-300 focus:ring-blue-500'
                                  }`}
                                  required
                                />
                                {errors.cvv && (
                                  <p className="text-red-500 text-xs mt-1">{errors.cvv}</p>
                                )}
                              </div>
                            </div>
                            
                            {/* Billing Zip Code */}
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Billing Zip Code <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                name="billingZipCode"
                                value={formData.billingZipCode}
                                onChange={handleChange}
                                placeholder="12345"
                                maxLength="10"
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${
                                  errors.billingZipCode
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-300 focus:ring-blue-500'
                                }`}
                                required
                              />
                              {errors.billingZipCode && (
                                <p className="text-red-500 text-xs mt-1">{errors.billingZipCode}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <h3 className="text-base font-semibold text-gray-800 mb-3">Bank Account Information</h3>
                          
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Account Holder Name <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                name="accountHolderName"
                                value={formData.accountHolderName}
                                onChange={handleChange}
                                placeholder="John Doe"
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${
                                  errors.accountHolderName
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-300 focus:ring-blue-500'
                                }`}
                                required
                              />
                              {errors.accountHolderName && (
                                <p className="text-red-500 text-xs mt-1">{errors.accountHolderName}</p>
                              )}
                            </div>
                            
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Routing Number <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                name="routingNumber"
                                value={formData.routingNumber}
                                onChange={handleChange}
                                placeholder="123456789"
                                maxLength="9"
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${
                                  errors.routingNumber
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-300 focus:ring-blue-500'
                                }`}
                                required
                              />
                              {errors.routingNumber && (
                                <p className="text-red-500 text-xs mt-1">{errors.routingNumber}</p>
                              )}
                            </div>
                            
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Account Number <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                name="accountNumber"
                                value={formData.accountNumber}
                                onChange={handleChange}
                                placeholder="1234567890"
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${
                                  errors.accountNumber
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-300 focus:ring-blue-500'
                                }`}
                                required
                              />
                              {errors.accountNumber && (
                                <p className="text-red-500 text-xs mt-1">{errors.accountNumber}</p>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Bank Name <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                name="bankName"
                                value={formData.bankName}
                                onChange={handleChange}
                                placeholder="Enter your bank name"
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${
                                  errors.bankName
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-300 focus:ring-blue-500'
                                }`}
                                required
                              />
                              {errors.bankName && (
                                <p className="text-red-500 text-xs mt-1">{errors.bankName}</p>
                              )}
                            </div>
                            
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Account Type <span className="text-red-500">*</span>
                              </label>
                              <select
                                name="accountType"
                                value={formData.accountType}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                required
                              >
                                <option value="checking">Checking</option>
                                <option value="savings">Savings</option>
                              </select>
                            </div>
                            
                            {/* Billing Zip Code */}
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Billing Zip Code <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                name="billingZipCode"
                                value={formData.billingZipCode}
                                onChange={handleChange}
                                placeholder="12345"
                                maxLength="10"
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${
                                  errors.billingZipCode
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-300 focus:ring-blue-500'
                                }`}
                                required
                              />
                              {errors.billingZipCode && (
                                <p className="text-red-500 text-xs mt-1">{errors.billingZipCode}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* reCAPTCHA */}
                  <div className="pt-4 mt-4 border-t border-gray-200">
                    <div className="text-xs font-semibold text-gray-900 mb-2">Verification</div>
                    <div className="flex justify-center relative z-50">
                      <div
                        id="recaptcha-container-event-payment"
                        className={`${errors.recaptcha ? 'ring-2 ring-red-500 rounded' : ''}`}
                      />
                    </div>
                    {errors.recaptcha && (
                      <p className="mt-2 text-red-500 text-sm text-center font-semibold">{errors.recaptcha}</p>
                    )}
                  </div>
                </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      </div>

      {/* Sticky Footer Buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-xl border-t border-gray-200 shadow-lg z-40">
        <div className="container mx-auto px-4 py-3">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={handleBack}
              className="px-6 py-2.5 rounded-full font-semibold text-sm bg-gray-200 hover:bg-gray-300 text-gray-700 transition-all duration-300 shadow-md hover:shadow-lg"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Back
            </button>
            
            <div className="flex-1 flex justify-center">
              <div className="text-xs text-gray-500">
                Total:
                <span className="ml-2 text-sm font-semibold text-gray-900">
                  ${totalPrice?.toFixed(2) || '0.00'}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                const form = document.querySelector('form');
                if (form) {
                  form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                }
              }}
              disabled={loading}
              className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 shadow-lg hover:shadow-xl ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white'
              }`}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Processing...
                </>
              ) : (
                <>
                  <i className="fas fa-lock mr-2"></i>
                  Pay ${totalPrice?.toFixed(2) || '0.00'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Processing Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center">
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md mx-4 text-center">
            <div className="mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-4 animate-pulse">
                <i className="fas fa-spinner fa-spin text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment</h3>
              <p className="text-gray-600 text-sm">
                Please wait while we securely process your payment...
              </p>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <i className="fas fa-lock text-green-600"></i>
              <span>Secure Transaction</span>
            </div>
          </div>
        </div>
      )}

      {/* Agreement Modal */}
      {isAgreementModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-3"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setIsAgreementModalOpen(false);
            }
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl border border-white/60 overflow-hidden">
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 bg-gradient-to-br from-white via-slate-50 to-violet-50">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Event Seva Agreement</div>
                <div className="text-xs text-gray-500">Please read before proceeding.</div>
              </div>
              <button
                type="button"
                onClick={() => setIsAgreementModalOpen(false)}
                className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 shadow-sm"
                aria-label="Close"
              >
                <i className="fas fa-times text-xs"></i>
              </button>
            </div>

            <div className="p-5">
              <div className="rounded-2xl bg-white border border-gray-200 px-4 py-4">
                <ul className="list-disc pl-5 text-xs text-gray-700 space-y-2">
                  <li>Seva / donation amounts are voluntary and may be non-refundable.</li>
                  <li>Seva allotment and timing may change based on temple schedule.</li>
                  <li>Please arrive on time and follow temple guidelines.</li>
                  <li>For questions, contact coordinators listed on the flyer.</li>
                </ul>
              </div>

              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsAgreementModalOpen(false)}
                  className="rounded-full bg-white hover:bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-700 border border-gray-200 shadow-sm"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsAgreementChecked(true);
                    setIsAgreementModalOpen(false);
                  }}
                  className="rounded-full bg-gradient-to-r from-indigo-600 via-purple-600 to-amber-500 px-4 py-2 text-xs font-semibold text-white shadow-md"
                >
                  I Agree
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      <SuccessModal 
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          dispatch(clearSelections());
          dispatch(logout());
          navigate('/');
        }}
        paymentResult={paymentResult}
      />
    </div>
  );
};

export default EventPayment;
